import { useRef, type KeyboardEvent } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatoCantidad } from '@/lib/utils/formato-precio';

export interface PestanaCompite<T extends string> {
  valor: T;
  texto: string;
  /** El contador al lado del nombre («Por decidir 12»). Sin número, nada. */
  cuenta?: number | null;
  /** `atencion` lo pinta como lo que espera a alguien, no como lo que sólo hay. */
  tonoCuenta?: 'neutro' | 'atencion';
  /** El dibujo antes del nombre (la maqueta: escudo en Reglas, rayo en Estrategias). */
  icono?: LucideIcon;
}

interface PestanasCompiteProps<T extends string> {
  pestanas: Array<PestanaCompite<T>>;
  valor: T;
  onCambiar: (valor: T) => void;
  /** Para el lector de pantalla: qué se elige con estas pestañas. */
  etiqueta: string;
  className?: string;
}

/**
 * Las pestañas grandes de la maqueta («Monitoreo de productos 4.407»): el nombre
 * con su contador y una raya con el degradado bajo la activa. Quien las usa
 * guarda la elegida en la URL: recargar o compartir el enlace vuelve acá.
 *
 * Teclado: flechas izquierda y derecha, Inicio y Fin (patrón de WAI-ARIA).
 */
export function PestanasCompite<T extends string>({
  pestanas,
  valor,
  onCambiar,
  etiqueta,
  className,
}: PestanasCompiteProps<T>) {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);

  const mover = (e: KeyboardEvent<HTMLButtonElement>, i: number) => {
    const n = pestanas.length;
    const destino =
      e.key === 'ArrowRight'
        ? (i + 1) % n
        : e.key === 'ArrowLeft'
          ? (i - 1 + n) % n
          : e.key === 'Home'
            ? 0
            : e.key === 'End'
              ? n - 1
              : null;
    if (destino === null) return;
    e.preventDefault();
    refs.current[destino]?.focus();
    onCambiar(pestanas[destino].valor);
  };

  return (
    <div
      role="tablist"
      aria-label={etiqueta}
      // En un celular entran las tres pestañas en un renglón: menos aire y letra un poco menor.
      className={cn(
        'flex flex-wrap items-end gap-x-5 gap-y-2 border-b border-border sm:gap-x-8',
        className
      )}
    >
      {pestanas.map((p, i) => {
        const activa = p.valor === valor;
        return (
          <button
            key={p.valor}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="button"
            role="tab"
            id={`pestana-${p.valor}`}
            aria-selected={activa}
            tabIndex={activa ? 0 : -1}
            onClick={() => onCambiar(p.valor)}
            onKeyDown={(e) => mover(e, i)}
            className={cn(
              'group relative -mb-px flex items-center gap-2 pb-3 pt-1 text-base font-semibold transition-colors sm:gap-2.5 sm:text-lg',
              'rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
              activa ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
            )}
          >
            {p.icono && <p.icono aria-hidden="true" className="h-4 w-4 shrink-0" />}
            <span className="tracking-tight">{p.texto}</span>
            {/* El espacio es para el nombre accesible («Reglas 9»); la separación la da `gap`. */}
            {p.cuenta != null && ' '}
            {p.cuenta != null && (
              <span
                className={cn(
                  'rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums',
                  p.tonoCuenta === 'atencion' && p.cuenta > 0
                    ? 'bg-alerta-suave text-alerta'
                    : 'bg-muted text-muted-foreground'
                )}
              >
                {formatoCantidad(p.cuenta)}
              </span>
            )}
            <span
              aria-hidden="true"
              className={cn(
                'bg-primary absolute inset-x-0 -bottom-px h-[3px] rounded-full transition-opacity',
                activa ? 'opacity-100' : 'opacity-0'
              )}
            />
          </button>
        );
      })}
    </div>
  );
}
